import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { EnvironmentInjector } from '@next/core-lib/environment';

import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { Environment } from '../../../environment';
import {
  GetReferenceValueSheetsResponse,
  UpsertRefValSheetResponse,
  ReferenceValueSheet,
  ReferenceValueSheetDataSeries,
  AnimalType,
  GetReferenceValueSheetKPIsResponse,
  ReferenceValueSheetKPIResults,
} from '../models/reference-value-sheet.model';

@Injectable({
  providedIn: 'root',
})
export class ReferenceValueSheetService {
  private baseUrl = `${this.env.API_BASE_URL}/service/poultry-feed-advice/secure/referenceValueSheet`;

  constructor(
    private readonly http: HttpClient,
    @Inject(EnvironmentInjector) private readonly env: Environment,
  ) {}

  public getReferenceValueSheetDataSeriesById(
    id: number,
  ): Observable<ReferenceValueSheetDataSeries | HttpErrorResponse> {
    return this.http.get<ReferenceValueSheetDataSeries>(`${this.baseUrl}/dataseries/${id}`).pipe(
      map((response) => response),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 404) {
          return of(undefined);
        } else {
          return throwError({ message: error.message });
        }
      }),
    );
  }

  public getReferenceValueSheets(
    country: string,
    animalType: AnimalType,
  ): Observable<ReferenceValueSheet[] | HttpErrorResponse> {
    return this.http
      .get<GetReferenceValueSheetsResponse>(
        `${this.baseUrl}/standard/${country}/type/${animalType.toString()}`,
      )
      .pipe(
        map((response) => response.referenceValueSheets),
        catchError((error: HttpErrorResponse) => {
          if (error.status === 404) {
            return of(<ReferenceValueSheet[]>[]);
          } else {
            return throwError({ message: error.message });
          }
        }),
      );
  }

  public getReferenceValueSheetKpis(
    id: number,
  ): Observable<ReferenceValueSheetKPIResults | HttpErrorResponse> {
    return this.http
      .post<GetReferenceValueSheetKPIsResponse>(`${this.baseUrl}/${id}/kpis`, {
        intervalType: 'Daily',
        kpis: ['ChickWeightInGrams', 'FeedIntakePerChick'],
        temperatureUnit: 'Celcius',
        feedUnit: 'Gram',
        waterUnit: 'ML',
        weightUnit: 'Gram',
      })
      .pipe(
        map((response) => ({
          refvalsheetId: id,
          kpis: response.kpis,
        })),
        catchError((error: HttpErrorResponse) => {
          if (error.status === 404) {
            return of(<ReferenceValueSheetKPIResults>{});
          } else {
            return throwError({ message: error.message });
          }
        }),
      );
  }

  public deleteReferenceValueSheet(id: number) {
    return this.http.delete(`${this.baseUrl}/${id}`);
  }

  public upsertReferenceValueSheet(
    refValSheet: ReferenceValueSheet,
  ): Observable<UpsertRefValSheetResponse | HttpErrorResponse> {
    return this.http.post<UpsertRefValSheetResponse>(`${this.baseUrl}`, refValSheet, {}).pipe(
      map((response) => response),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 404) {
          return of(<UpsertRefValSheetResponse>{});
        } else {
          return throwError({ message: error.message });
        }
      }),
    );
  }

  public copyReferenceValueSheetAsNew(
    refValSheetId: number,
  ): Observable<UpsertRefValSheetResponse | HttpErrorResponse> {
    return this.http
      .post<UpsertRefValSheetResponse>(`${this.baseUrl}/${refValSheetId}`, { country: 'NL' })
      .pipe(
        map((response) => response),
        catchError((error: HttpErrorResponse) => {
          if (error.status === 404) {
            return of(<UpsertRefValSheetResponse>{});
          } else {
            return throwError({ message: error.message });
          }
        }),
      );
  }
}
